import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Skeleton,
  Typography,
  useTheme
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useQuery } from "@tanstack/react-query";
import {
  BRAND_COLOR,
  useKivunovaTranslation,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import {
  PRODUCT_INVENTORY_STATUS_ACCENTS,
  PRODUCT_INVENTORY_STATUS_ORDER
} from "./constants/productInventoryStatusConfig";
import {
  ProductInventoryFilterKey,
  ProductInventoryStatCardConfig,
  ProductInventorySummaryProps
} from "./types/products";
import { fetchAllProductItemsByProduct } from "./utils/fetchAllProductItemsByProduct";
import { aggregateProductInventoryStats } from "./utils/productInventoryStats";
import {
  getProductInventoryStatCardDescription,
  getProductUnitLabel
} from "./utils/getProductInventoryStatCardDescription";

const SKELETON_CARD_COUNT = PRODUCT_INVENTORY_STATUS_ORDER.length + 1;

const ProductInventorySummary = ({
  product,
  activeFilter = "",
  onStatCardClick
}: ProductInventorySummaryProps) => {
  const { t } = useKivunovaTranslation();
  const { setAlert } = useContext(AlertBannerContext);
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = useState(false);

  const { data: items = [], isPending } = useQuery({
    queryKey: ["productInventorySummary", product.id],
    queryFn: () => fetchAllProductItemsByProduct(product.id, setAlert),
    enabled: !!product.id
  });

  const unitLabel = useMemo(
    () => getProductUnitLabel(product, t),
    [product, t]
  );

  const statCards: ProductInventoryStatCardConfig[] = useMemo(() => {
    const stats = aggregateProductInventoryStats(items, {
      sellingMode: product.sellingMode,
      isPartialSellingAllowed: product.isPartialSellingAllowed
    });
    const describe = (filterKey: ProductInventoryFilterKey) =>
      getProductInventoryStatCardDescription(
        filterKey,
        product.sellingMode,
        unitLabel,
        t
      );

    const totalCard: ProductInventoryStatCardConfig = {
      key: "total",
      filterKey: "total",
      label: t("product-inventory-total-items", "Total items"),
      value: stats.totalItems,
      accent: BRAND_COLOR,
      testId: "product-inventory-stat-total"
    };

    const statusCards = PRODUCT_INVENTORY_STATUS_ORDER.map((status) => ({
      key: status,
      filterKey: status,
      label: describe(status),
      value: stats.byStatus[status],
      accent: PRODUCT_INVENTORY_STATUS_ACCENTS[status],
      testId: `product-inventory-stat-${status.toLowerCase()}`
    }));

    return [totalCard, ...statusCards];
  }, [
    items,
    product.sellingMode,
    product.isPartialSellingAllowed,
    t,
    unitLabel
  ]);

  const isCardSelected = (card: ProductInventoryStatCardConfig) =>
    card.filterKey === "total"
      ? activeFilter === ""
      : activeFilter === card.filterKey;

  const handleCardActivate = (filterKey: ProductInventoryFilterKey) => {
    if (isMobileOrTablet) {
      setExpanded(true);
    }
    onStatCardClick?.(filterKey);
  };

  const renderCardsGrid = () => (
    <Grid container spacing={1}>
      {statCards.map((card) => {
        const selected = isCardSelected(card);
        const clickable = Boolean(onStatCardClick);

        return (
          <Grid key={card.key} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
            <Box
              data-testid={card.testId}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-pressed={clickable ? selected : undefined}
              onClick={
                clickable ? () => handleCardActivate(card.filterKey) : undefined
              }
              onKeyDown={
                clickable
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleCardActivate(card.filterKey);
                      }
                    }
                  : undefined
              }
              sx={{
                position: "relative",
                overflow: "hidden",
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                minHeight: 64,
                bgcolor: selected ? `${card.accent}10` : "background.paper",
                border: "1px solid",
                borderColor: selected ? card.accent : "divider",
                boxShadow: selected
                  ? `0 0 0 1px ${card.accent}33`
                  : "0 1px 3px rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 0.25,
                cursor: clickable ? "pointer" : "default",
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  bgcolor: card.accent,
                  opacity: 0.9
                },
                ...(clickable && {
                  "&:hover": {
                    borderColor: card.accent,
                    boxShadow: `0 2px 8px ${card.accent}22`
                  }
                })
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
                sx={{
                  pl: 0.5,
                  lineHeight: 1.2,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}
              >
                {card.label}
              </Typography>
              <Typography
                variant="h6"
                component="p"
                fontWeight={700}
                sx={{
                  pl: 0.5,
                  color: "text.primary",
                  lineHeight: 1.1,
                  fontVariantNumeric: "tabular-nums"
                }}
              >
                {card.value.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderSkeletonGrid = () => (
    <Grid container spacing={1}>
      {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
        <Grid key={index} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
          <Skeleton variant="rounded" height={64} sx={{ borderRadius: 1.5 }} />
        </Grid>
      ))}
    </Grid>
  );

  if (!isPending && items.length === 0) {
    return null;
  }

  const toggleLabel = expanded
    ? t("product-inventory-summary-hide", "Hide stock summary")
    : t("product-inventory-summary-show", "View stock summary");

  const cardsContent = isPending ? renderSkeletonGrid() : renderCardsGrid();

  return (
    <Box
      data-testid="product-inventory-summary"
      sx={{ mb: 2 }}
      aria-label={t(
        "product-inventory-summary-aria",
        "Product inventory quick view"
      )}
    >
      {isMobileOrTablet && (
        <Button
          data-testid="product-inventory-summary-toggle"
          variant="outlined"
          fullWidth
          disabled={isPending}
          onClick={() => setExpanded((prev) => !prev)}
          startIcon={<Inventory2OutlinedIcon />}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          aria-expanded={expanded}
          sx={{
            mb: expanded ? 1.5 : 0,
            textTransform: "none",
            color: BRAND_COLOR,
            borderColor: BRAND_COLOR,
            "&:hover": {
              borderColor: BRAND_COLOR,
              bgcolor: `${BRAND_COLOR}08`
            }
          }}
        >
          {isPending
            ? t("product-inventory-summary-loading", "Loading stock summary...")
            : toggleLabel}
        </Button>
      )}

      {(!isMobileOrTablet || expanded) && (
        <Box sx={{ mt: isMobileOrTablet ? 1.5 : 0 }}>{cardsContent}</Box>
      )}
    </Box>
  );
};

export default ProductInventorySummary;
